/**
 * HelpLauncher — unified floating Help button merging:
 *   • "Ask" tab  — streaming chat with the guide assistant (AskPanel)
 *   • "Report a bug" tab — bug/feature feedback form (ReportBugPanel)
 *
 * ALL state lives here so switching tabs or closing/reopening the panel within
 * a session never loses an in-progress chat or half-written bug report.
 * The panel is never unmounted while streaming is in flight.
 */

import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router';
import { LifeBuoy, X } from 'lucide-react';
import { useIsMobile } from '../../hooks/useIsMobile';
import AskPanel, { type ChatMessage } from './AskPanel';
import ReportBugPanel, { type ReportBugState } from './ReportBugPanel';
import { HELP_OPEN_EVENT, type HelpTab } from './openHelp';

const PRIMARY = 'var(--primary)';

type Tab = 'ask' | 'report';

const DEFAULT_BUG_STATE: ReportBugState = {
  type: 'bug',
  title: '',
  description: '',
  result: null,
};

export default function HelpLauncher() {
  const location = useLocation();
  const isMobile = useIsMobile();
  const pageUrl = location.pathname + location.search;

  // Panel open/close — never close while streaming
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('ask');

  // ── Ask chat state ─────────────────────────────────────────────────────────
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [draft, setDraft] = useState('');
  const abortRef = useRef<AbortController | null>(null);

  // ── Report bug state ───────────────────────────────────────────────────────
  const [bugState, setBugState] = useState<ReportBugState>(DEFAULT_BUG_STATE);
  const [bugSubmitting, setBugSubmitting] = useState(false);
  const feedbackCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (feedbackCloseTimerRef.current) clearTimeout(feedbackCloseTimerRef.current);
  }, []);

  function clearFeedbackCloseTimer() {
    if (feedbackCloseTimerRef.current) {
      clearTimeout(feedbackCloseTimerRef.current);
      feedbackCloseTimerRef.current = null;
    }
  }

  // Opened from the mobile plus menu, which is the only way in on mobile
  // now that this component has no button of its own there (#1399).
  useEffect(() => {
    function onExternalOpen(event: Event) {
      const requested = (event as CustomEvent<HelpTab>).detail;
      if (requested === 'ask' || requested === 'report') setTab(requested);
      clearFeedbackCloseTimer();
      setOpen(true);
    }
    window.addEventListener(HELP_OPEN_EVENT, onExternalOpen);
    return () => window.removeEventListener(HELP_OPEN_EVENT, onExternalOpen);
  }, []);

  function handleOpen() {
    clearFeedbackCloseTimer();
    setOpen(true);
  }

  function handleClose() {
    // Never close while a stream is in flight — the panel must stay mounted
    if (streaming) return;
    if (feedbackCloseTimerRef.current) {
      clearFeedbackCloseTimer();
      setBugState(DEFAULT_BUG_STATE);
    }
    setOpen(false);
  }

  function handleFeedbackSubmitted() {
    clearFeedbackCloseTimer();
    feedbackCloseTimerRef.current = setTimeout(() => {
      setOpen(false);
      setBugState(DEFAULT_BUG_STATE);
      feedbackCloseTimerRef.current = null;
    }, 1500);
  }

  function handleTabSwitch(next: Tab) {
    if (next === tab) return;
    // Abort any running stream when leaving the Ask tab
    if (tab === 'ask' && next !== 'ask' && abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
      setStreaming(false);
    }
    setTab(next);
  }

  function handleBackdropMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }

  // Lifted setter for AskPanel that accepts either a value or an updater function
  function handleMessagesChange(
    next: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[]),
  ) {
    if (typeof next === 'function') {
      setMessages(next);
    } else {
      setMessages(next);
    }
  }

  return (
    <>
      {/* Floating button — desktop only.
          #1399: on mobile this was a third floating control in the same
          right-hand column as the plus button, and it landed exactly on the
          first item the plus menu fans out (Clock In) -- 84px vs 80px above the
          tab bar. Stacking it higher, as #1388 did, only moved which control it
          covered. Ask and Report a bug are items in the plus menu instead, so
          there is one floating control on mobile rather than two. */}
      {!isMobile && (
      <button
        onClick={handleOpen}
        title="Help"
        style={{
          position: 'fixed',
          // Desktop has no tab bar and no plus button to collide with, so a
          // flat offset is correct here. The mobile branch this used to carry
          // is gone with the mobile button itself (#1399).
          bottom: 24,
          right: 24,
          zIndex: 999,
          width: 48,
          height: 48,
          borderRadius: '50%',
          background: 'var(--gradient-primary)',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          color: 'var(--text-inverse)',
          padding: 0,
          transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
          transform: open ? 'scale(0.9) rotate(-10deg)' : 'scale(1)',
        }}
      >
        <LifeBuoy size={22} />
      </button>
      )}

      {/* Panel — kept in DOM while streaming so the stream isn't lost on close */}
      <div
        onMouseDown={handleBackdropMouseDown}
        style={{
          position: 'fixed',
          inset: 0,
          background: open ? 'rgba(0,0,0,0.45)' : 'transparent',
          zIndex: open ? 1000 : -1,
          pointerEvents: open ? 'auto' : 'none',
          opacity: open ? 1 : 0,
          backdropFilter: 'blur(3px)',
          transition: 'opacity 0.3s ease, backdrop-filter 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Help"
          // Isolate keyboard + paste events so app hotkeys don't fire while typing
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Escape' && !streaming) handleClose();
          }}
          onPaste={(e) => e.stopPropagation()}
          style={{
            background: 'var(--surface-overlay-strong)',
            backdropFilter: 'blur(24px) saturate(150%)',
            border: '1px solid var(--border, rgba(128,128,128,0.12))',
            borderRadius: 14,
            width: 480,
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 'calc(100vh - 64px)',
            boxShadow: '0 24px 60px rgba(0, 0, 0, 0.12), 0 8px 24px rgba(0, 0, 0, 0.08)',
            display: 'flex',
            flexDirection: 'column',
            color: 'var(--text-primary)',
            overflow: 'hidden',
            // Premium enter/exit transitions
            opacity: open ? 1 : 0,
            pointerEvents: open ? 'auto' : 'none',
            transform: open ? 'scale(1) translateY(0)' : 'scale(0.96) translateY(12px)',
            transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '14px 20px 0',
              flexShrink: 0,
            }}
          >
            <div style={{ display: 'flex', gap: 4 }}>
              {(['ask', 'report'] as Tab[]).map((t) => {
                const label = t === 'ask' ? 'Ask' : 'Report a bug';
                const active = tab === t;
                return (
                  <button
                    key={t}
                    onClick={() => handleTabSwitch(t)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '6px 6px 0 0',
                      border: 'none',
                      background: active ? PRIMARY : 'var(--surface-strong)',
                      color: active ? 'var(--text-inverse)' : 'var(--text-secondary)',
                      fontWeight: active ? 600 : 400,
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            <button
              onClick={handleClose}
              disabled={streaming}
              title="Close"
              style={{
                background: 'none',
                border: 'none',
                cursor: streaming ? 'not-allowed' : 'pointer',
                color: 'var(--text-muted)',
                padding: 4,
                display: 'flex',
                alignItems: 'center',
                opacity: streaming ? 0.5 : 1,
              }}
            >
              <X size={18} />
            </button>
          </div>

          {/* Tab divider */}
          <div style={{ height: 1, background: 'var(--border)', flexShrink: 0, marginTop: 0 }} />

          {/* Panel body */}
          <div style={{ flex: 1, minHeight: 0, padding: '16px 20px 20px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {tab === 'ask' ? (
              <AskPanel
                messages={messages}
                streaming={streaming}
                abortRef={abortRef}
                onMessagesChange={handleMessagesChange}
                onStreamingChange={setStreaming}
                draft={draft}
                onDraftChange={setDraft}
              />
            ) : (
              <ReportBugPanel
                pageUrl={pageUrl}
                state={bugState}
                onChange={(patch) => setBugState((prev) => ({ ...prev, ...patch }))}
                submitting={bugSubmitting}
                onSubmittingChange={setBugSubmitting}
                onSubmitted={handleFeedbackSubmitted}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
